import React, { useState, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';
import { ChangelogModal } from './organisms/ChangelogModal';
import { QuizModal } from './organisms/QuizModal';
import { ConductRulesModal } from './organisms/DuringMatch/modals/ConductRulesModal';
import { EditMatchModal } from './organisms/EditMatchModal';
import { BurgerMenu } from './atoms/BurgerMenu';
import { getVersionInfo } from '../utils/version';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { MatchState, MatchAction, MatchInfo } from '../types';
import { MANUAL_MATCH_PREFIX } from '../constants';
import * as matchStorage from '../storage/matchStorage';
import { AppMode, getMenuVisibility, useAppConfig } from '../hooks/useAppConfig';

interface ChangelogProviderProps {
  children: React.ReactNode;
  matchState?: MatchState;
  dispatch?: React.Dispatch<MatchAction>;
  /** Decides which menu entries show (config.json `ui.<mode>`); standalone when absent. */
  appMode?: AppMode;
}

export function ChangelogProvider({ children, matchState, dispatch, appMode = 'standalone' }: ChangelogProviderProps) {
  const pwa = usePWA();
  const { t } = useTranslation();
  const { config } = useAppConfig();
  const menuVisibility = getMenuVisibility(config, appMode);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showNewMatchConfirm, setShowNewMatchConfirm] = useState(false);
  const [showEditMatch, setShowEditMatch] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const versionInfo = getVersionInfo();

  // Determine if edit match should be visible
  // Only for manual matches (matchId starts with MANUAL_MATCH_PREFIX) on 'before' or 'during' screens
  const isManualMatch = matchState?.matchInfo?.matchId?.startsWith(MANUAL_MATCH_PREFIX) ?? false;
  const isEditableScreen = matchState?.currentScreen === 'before' || matchState?.currentScreen === 'during';
  const canEditMatch = isManualMatch && isEditableScreen && menuVisibility.editMatch;

  // État pour tracking des versions vues
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(
    localStorage.getItem('lastSeenVersion')
  );

  // Fonction pour ouvrir le changelog manuellement
  const handleOpenChangelog = () => {
    setShowChangelog(true);
  };

  // Fonction pour ouvrir les règles
  const handleOpenRules = () => {
    setShowRules(true);
  };

  const handleCloseRules = () => {
    setShowRules(false);
  };

  // Fonction pour nouveau match
  const handleNewMatch = () => {
    setShowNewMatchConfirm(true);
  };

  const handleConfirmNewMatch = () => {
    // Nettoyer toutes les sauvegardes de match et recharger
    matchStorage.clearAll();
    window.location.href = '/';
  };

  const handleCancelNewMatch = () => {
    setShowNewMatchConfirm(false);
  };

  // Fonction pour éditer le match
  const handleEditMatch = () => {
    setShowEditMatch(true);
  };

  const handleSaveMatchInfo = (updatedInfo: Partial<MatchInfo>) => {
    if (dispatch) {
      dispatch({ type: 'UPDATE_MATCH_INFO', matchInfo: updatedInfo });
    }
    setShowEditMatch(false);
  };

  const handleCloseEditMatch = () => {
    setShowEditMatch(false);
  };

  // Détection automatique nouvelle version
  useEffect(() => {
    if (pwa.isServiceWorkerUpdated && versionInfo.buildNumber !== lastSeenVersion) {
      setShowChangelog(true);
    }
  }, [pwa.isServiceWorkerUpdated, versionInfo.buildNumber, lastSeenVersion]);

  const handleChangelogClose = () => {
    setShowChangelog(false);
    localStorage.setItem('lastSeenVersion', versionInfo.buildNumber);
    setLastSeenVersion(versionInfo.buildNumber);
  };

  return (
    <>
      {children}

      {/* Burger Menu */}
      <BurgerMenu
        onNewMatch={handleNewMatch}
        onOpenChangelog={handleOpenChangelog}
        onOpenRules={handleOpenRules}
        onOpenQuiz={() => setShowQuiz(true)}
        onEditMatch={handleEditMatch}
        showEditMatch={canEditMatch}
        visibility={menuVisibility}
      />

      <QuizModal isOpen={showQuiz} onClose={() => setShowQuiz(false)} />

      {/* Modal changelog */}
      {showChangelog && (
        <ChangelogModal
          isOpen={showChangelog}
          onClose={handleChangelogClose}
          lastSeenVersion={lastSeenVersion}
        />
      )}

      {/* Modal rules */}
      <ConductRulesModal
        isOpen={showRules}
        onClose={handleCloseRules}
      />

      {/* Modal confirmation nouveau match */}
      {showNewMatchConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('menu.confirmNewMatch.title')}
            </h3>
            <p className="text-gray-600 mb-6">
              {t('menu.confirmNewMatch.message')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelNewMatch}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                {t('menu.confirmNewMatch.cancel')}
              </button>
              <button
                onClick={handleConfirmNewMatch}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                {t('menu.confirmNewMatch.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Match Modal */}
      {matchState && (
        <EditMatchModal
          isOpen={showEditMatch}
          matchInfo={matchState.matchInfo}
          currentScreen={matchState.currentScreen}
          onClose={handleCloseEditMatch}
          onSave={handleSaveMatchInfo}
        />
      )}
    </>
  );
}
